/**
 * Theme-Aware Gradient & Glow Definitions
 *
 * Provides gradient/glow configs for GradientBackground based on active theme.
 */
import type { ThemeMode } from './types';
import { darkTheme } from './darkTheme';
import { lightTheme } from './lightTheme';
import { PRIMITIVE_COLORS } from '../../residents/residentColors';

// Base glow hues — sourced from primitives to avoid duplication
const GLOW_HUES = {
  blue: PRIMITIVE_COLORS.resioBlueBorder,     // #4773FE
  purple: PRIMITIVE_COLORS.resioPurple,        // #BE5EC8
  red: PRIMITIVE_COLORS.resioPink,             // #F55560
  // Derived mid-tones (not in the primitive palette)
  blueDarkMid: '#243a7f',
  redDarkMid: '#7b2b30',
  blueLightMid: '#8ba8ff',
  purpleLightMuted: '#d4a5db',
  redLightMuted: '#f7a0a7',
} as const;

export interface GlowStop {
  offset: number;
  color: string;
  opacity: number;
}

export interface GlowDef {
  id: string;
  cx: number;
  cy: number;
  rx: number;
  ry: number;
  stops: GlowStop[];
}

export function getGlows(
  mode: ThemeMode,
  screenWidth: number,
  screenHeight: number,
): GlowDef[] {
  if (mode === 'dark') {
    return [
      {
        id: 'blueLeft',
        cx: screenWidth * 0.15,
        cy: screenHeight * 0.02,
        rx: screenWidth * 0.7,
        ry: screenHeight * 0.35,
        stops: [
          { offset: 0, color: GLOW_HUES.blue, opacity: 0.35 },
          { offset: 0.5, color: GLOW_HUES.blueDarkMid, opacity: 0.18 },
          { offset: 1, color: GLOW_HUES.blue, opacity: 0 },
        ],
      },
      {
        id: 'blueRight',
        cx: screenWidth * 0.85,
        cy: screenHeight * 0.02,
        rx: screenWidth * 0.7,
        ry: screenHeight * 0.35,
        stops: [
          { offset: 0, color: GLOW_HUES.blue, opacity: 0.35 },
          { offset: 0.5, color: GLOW_HUES.blueDarkMid, opacity: 0.18 },
          { offset: 1, color: GLOW_HUES.blue, opacity: 0 },
        ],
      },
      {
        id: 'purple',
        cx: screenWidth * 0.5,
        cy: screenHeight * 0.45,
        rx: screenWidth * 0.7,
        ry: screenHeight * 0.3,
        stops: [
          { offset: 0, color: GLOW_HUES.purple, opacity: 0.28 },
          { offset: 0.6, color: GLOW_HUES.purple, opacity: 0.12 },
          { offset: 1, color: GLOW_HUES.purple, opacity: 0 },
        ],
      },
      {
        id: 'red',
        cx: screenWidth * 0.5,
        cy: screenHeight,
        rx: screenWidth * 0.65,
        ry: screenHeight * 0.3,
        stops: [
          { offset: 0, color: GLOW_HUES.red, opacity: 0.35 },
          { offset: 0.5, color: GLOW_HUES.redDarkMid, opacity: 0.15 },
          { offset: 1, color: GLOW_HUES.red, opacity: 0 },
        ],
      },
    ];
  }

  // Light theme: prominent pastel glows
  return [
    {
      id: 'blueLeft',
      cx: screenWidth * 0.15,
      cy: screenHeight * 0.02,
      rx: screenWidth * 0.7,
      ry: screenHeight * 0.35,
      stops: [
        { offset: 0, color: GLOW_HUES.blue, opacity: 0.30 },
        { offset: 0.5, color: GLOW_HUES.blueLightMid, opacity: 0.16 },
        { offset: 1, color: GLOW_HUES.blue, opacity: 0 },
      ],
    },
    {
      id: 'blueRight',
      cx: screenWidth * 0.85,
      cy: screenHeight * 0.02,
      rx: screenWidth * 0.7,
      ry: screenHeight * 0.35,
      stops: [
        { offset: 0, color: GLOW_HUES.blue, opacity: 0.30 },
        { offset: 0.5, color: GLOW_HUES.blueLightMid, opacity: 0.16 },
        { offset: 1, color: GLOW_HUES.blue, opacity: 0 },
      ],
    },
    {
      id: 'purple',
      cx: screenWidth * 0.5,
      cy: screenHeight * 0.45,
      rx: screenWidth * 0.7,
      ry: screenHeight * 0.3,
      stops: [
        { offset: 0, color: GLOW_HUES.purpleLightMuted, opacity: 0.24 },
        { offset: 0.6, color: GLOW_HUES.purpleLightMuted, opacity: 0.10 },
        { offset: 1, color: GLOW_HUES.purpleLightMuted, opacity: 0 },
      ],
    },
    {
      id: 'red',
      cx: screenWidth * 0.5,
      cy: screenHeight,
      rx: screenWidth * 0.65,
      ry: screenHeight * 0.3,
      stops: [
        { offset: 0, color: GLOW_HUES.redLightMuted, opacity: 0.24 },
        { offset: 0.5, color: GLOW_HUES.redLightMuted, opacity: 0.10 },
        { offset: 1, color: GLOW_HUES.redLightMuted, opacity: 0 },
      ],
    },
  ];
}

export function getGradientColors(mode: ThemeMode): {
  gradientStart: string;
  gradientEnd: string;
} {
  const theme = mode === 'dark' ? darkTheme : lightTheme;
  return {
    gradientStart: theme.redesign.bg.gradientStart,
    gradientEnd: theme.redesign.bg.base,
  };
}

export function getCssRadialGlows(mode: ThemeMode): string {
  if (mode === 'dark') {
    return [
      'radial-gradient(ellipse 70% 35% at 15% 2%, rgba(71,115,254,0.35) 0%, rgba(36,58,127,0.18) 50%, transparent 100%)',
      'radial-gradient(ellipse 70% 35% at 85% 2%, rgba(71,115,254,0.35) 0%, rgba(36,58,127,0.18) 50%, transparent 100%)',
      'radial-gradient(ellipse 70% 30% at 50% 45%, rgba(190,94,200,0.28) 0%, rgba(190,94,200,0.12) 60%, transparent 100%)',
      'radial-gradient(ellipse 65% 30% at 50% 100%, rgba(245,85,96,0.35) 0%, rgba(123,43,48,0.15) 50%, transparent 100%)',
    ].join(', ');
  }

  return [
    'radial-gradient(ellipse 70% 35% at 15% 2%, rgba(71,115,254,0.30) 0%, rgba(139,168,255,0.16) 50%, transparent 100%)',
    'radial-gradient(ellipse 70% 35% at 85% 2%, rgba(71,115,254,0.30) 0%, rgba(139,168,255,0.16) 50%, transparent 100%)',
    'radial-gradient(ellipse 70% 30% at 50% 45%, rgba(212,165,219,0.24) 0%, rgba(212,165,219,0.10) 60%, transparent 100%)',
    'radial-gradient(ellipse 65% 30% at 50% 100%, rgba(247,160,167,0.24) 0%, rgba(247,160,167,0.10) 50%, transparent 100%)',
  ].join(', ');
}

export const getResidentHeroGradient = (mode: 'light' | 'dark') => {
  if (mode === 'dark') {
    return ['rgba(70,114,254,0.18)', 'rgba(181,140,255,0.14)', 'rgba(255,107,156,0.10)'] as const;
  }
  return ['rgba(70,114,254,0.08)', 'rgba(181,140,255,0.06)', 'rgba(255,107,156,0.05)'] as const;
};
